import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const Menu = () => {
  const [activeTab, setActiveTab] = useState('cold-buffet');

  const menuCategories = {
    'cold-buffet': {
      title: 'Cold Buffets',
      description: 'Perfect for any occasion - fresh, delicious, and beautifully presented',
      price: 'From £6 per person',
      items: [
        { name: 'Assorted Sandwiches', description: 'Chicken mayo, tuna, egg mayo, cheese & tomato' },
        { name: 'Wraps & Rolls', description: 'Filled with your choice of meats, cheeses, and salads' },
        { name: 'Greek Salad', description: 'Fresh tomatoes, cucumber, feta, olives, red onion' },
        { name: 'Pasta Salad', description: 'Homemade with seasonal vegetables and herbs' },
        { name: 'Quiche Selection', description: 'Lorraine, cheese & onion, vegetable' },
        { name: 'Cold Meat Platter', description: 'Selection of quality cold cuts and cheeses' },
        { name: 'Prawn Cocktail', description: 'Fresh prawns with Marie Rose sauce' },
        { name: 'Sausage Rolls', description: 'Homemade with quality pork' },
        { name: 'Cocktail Sausages', description: 'Honey glazed' },
        { name: 'Dessert Selection', description: 'Gateaux, trifles, cheesecakes' }
      ]
    },
    'hot-buffet': {
      title: 'Hot Buffets',
      description: 'Hearty, warming dishes that impress every time',
      price: 'From £6 per person',
      items: [
        { name: 'Chilli Con Carne', description: 'Slow-cooked with rice and tortilla chips' },
        { name: 'Lasagne', description: 'Traditional beef lasagne with garlic bread' },
        { name: 'Chicken Curry', description: 'Mild or medium, served with rice and naan' },
        { name: 'Roast Beef', description: 'Tender roast with Yorkshire puddings and gravy' },
        { name: 'Chicken Fajitas', description: 'With peppers, onions, wraps and accompaniments' },
        { name: 'Stuffed Peppers', description: 'Vegetarian option with rice and herbs' },
        { name: 'Shepherd\'s Pie', description: 'Traditional recipe with creamy mash topping' },
        { name: 'Meatballs', description: 'In rich tomato sauce' },
        { name: 'Jacket Potatoes', description: 'With various fillings' },
        { name: 'Seasonal Vegetables', description: 'Fresh and locally sourced' }
      ]
    },
    'dietary': {
      title: 'Dietary Options',
      description: 'We cater to all dietary requirements',
      price: 'No extra charge',
      items: [
        { name: 'Vegetarian Options', description: 'Wide range of meat-free dishes' },
        { name: 'Vegan Menus', description: 'Plant-based alternatives available' },
        { name: 'Gluten-Free', description: 'Coeliac-friendly options on request' },
        { name: 'Halal Catering', description: 'Certified halal meat available' },
        { name: 'Allergy-Friendly', description: 'Nut-free, dairy-free, and more' },
        { name: 'Kids Menus', description: 'Child-friendly portions and options' }
      ]
    }
  };

  return (
    <section id="menu" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-base)' }}>
            Our Menu
          </h2>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--text-subtle-light)' }}>
            All menus are customizable to suit your preferences, budget, and guest count.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="cold-buffet">Cold Buffets</TabsTrigger>
            <TabsTrigger value="hot-buffet">Hot Buffets</TabsTrigger>
            <TabsTrigger value="dietary">Dietary Options</TabsTrigger>
          </TabsList>

          {Object.entries(menuCategories).map(([key, category]) => (
            <TabsContent key={key} value={key}>
              <Card>
                <CardHeader className="text-center">
                  <CardTitle className="text-3xl" style={{ color: 'var(--text-base)' }}>
                    {category.title}
                  </CardTitle>
                  <CardDescription className="text-lg">
                    {category.description}
                  </CardDescription>
                  <div className="mt-4">
                    <span
                      className="inline-block px-6 py-2 rounded-full text-white font-semibold text-lg"
                      style={{ backgroundColor: 'var(--accent-ui-accent)' }}
                    >
                      {category.price}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {category.items.map((item, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-lg transition-all card-hover"
                        style={{ backgroundColor: 'var(--color-accent-coconut)' }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-bold text-lg" style={{ color: 'var(--text-base)' }}>
                            {item.name}
                          </h4>
                          {item.badge && (
                            <Badge
                              variant="secondary"
                              style={{ backgroundColor: 'var(--accent-ui-accent)', color: 'white' }}
                            >
                              {item.badge}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm" style={{ color: 'var(--text-subtle-light)' }}>
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-12 text-center">
          <div
            className="inline-block p-6 rounded-xl"
            style={{ backgroundColor: 'var(--color-accent-coconut)' }}
          >
            <p className="text-lg font-semibold mb-2" style={{ color: 'var(--text-base)' }}>
              Buffet events ranging from £6-12 per person
            </p>
            <p style={{ color: 'var(--text-subtle-light)' }}>
              All our food is prepared fresh using locally-sourced ingredients
            </p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-subtle-light)' }}>
              Minimum order: 20 people | Full menus available via phone or Instagram DM
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Menu;